/**
 * @format
 */
import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FudisDialogService } from '@funidata/ngx-fudis';
import { PlanStudyRightService, PlanValidationTs, ValidatablePlan } from 'common-typescript';
import {
    Attainment,
    CourseUnit,
    CourseUnitResultItem,
    CurriculumPeriod,
    CustomCourseUnitAttainment,
    CustomModuleAttainment,
    CustomStudyDraft,
    DegreeProgramme,
    GroupingModule,
    Module,
    OtmId,
    Plan,
    StudyModule,
    StudyRight,
} from 'common-typescript/types';
import * as _ from 'lodash-es';
import {
    combineLatestWith,
    EMPTY,
    exhaustMap,
    map,
    merge,
    mergeMap,
    Observable,
    of,
    ReplaySubject,
    scan,
    shareReplay,
    Subject,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from 'rxjs';
import {
    AppErrorHandler,
    confirmDialogFdOpener,
    ConfirmDialogFdValues,
    CurriculumPeriodEntityService,
    GradeScaleEntityService,
    PLAN_ACTIONS_SERVICE_INJECTION_TOKEN,
    PlanActionsService,
    PlanData,
    PlanDiffService,
    PlanEntityService,
    PlanManager,
    PlanStateObject,
    PlanStateService,
    RawPlanEditService,
    sisuChain,
    UiOperation,
    UiOperationType,
    UniversityService,
} from 'sis-components';
import { PlanDataService, PlanStructureData } from '../../../../../plan-ng/plan-data.service';
import { StudentPlanActionsService } from '../../../../service/student-plan-actions.service';
import { StudentPlanUiActionsServiceImplementation } from '../../../../service/student-plan-ui-actions-service-implementation.service';
import { PlanStructureEditModalValues } from '../plan-structure-edit-modal.component';

export interface FreeEditPlanData {
    planData: PlanData;
    allModules: Module[];
    searchCurriculumPeriods: CurriculumPeriod[];
    allCourseUnits: CourseUnit[];
    allCustomCourseUnitAttainments: CustomCourseUnitAttainment[];
    allCustomStudyDrafts: CustomStudyDraft[];
    planStateObject: PlanStateObject;
    validatablePlan: ValidatablePlan;
    allCustomModuleAttainments: CustomModuleAttainment[];
}

@Component({
    selector: 'app-plan-structure-free-edit',
    templateUrl: './plan-structure-free-edit.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [
        PlanManager,
        {
            provide: PLAN_ACTIONS_SERVICE_INJECTION_TOKEN,
            useClass: StudentPlanActionsService,
        },
    ],
    standalone: false,
})
export class PlanStructureFreeEditComponent implements OnInit {
    private dialogService = inject(FudisDialogService);
    private confirmDialogFdOpener = confirmDialogFdOpener();
    private studentPlanUIActionsImplementationService = inject(StudentPlanUiActionsServiceImplementation);
    modalValues: PlanStructureEditModalValues & {
        handleOpenAttainmentDetailsModalUiOperation?: (courseUnit: CourseUnit) => void;
    } = inject(MAT_DIALOG_DATA);
    submitClick$: Subject<void> = new Subject();

    data$: Observable<FreeEditPlanData>;

    planIdInputSubject$ = new ReplaySubject<OtmId>(1);
    planStructureData$: Observable<PlanStructureData>;
    dataInput$: Observable<[OtmId, Plan[], StudyRight[]]>;

    unattachedAttainments: Attainment[] = [];
    isPlanRootModule: boolean;
    originalValidatablePlan: ValidatablePlan;
    currentUniversityOrgId: OtmId;

    constructor(
        private planEntityService: PlanEntityService,
        private destroyRef: DestroyRef,
        private planManager: PlanManager,
        private planStateService: PlanStateService,
        private readonly planDataService: PlanDataService,
        private readonly gradeScaleEntityService: GradeScaleEntityService,
        private readonly rawPlanEditService: RawPlanEditService,
        private readonly planDiffService: PlanDiffService,
        private readonly appErrorHandler: AppErrorHandler,
        private readonly universityService: UniversityService,
        @Inject(PLAN_ACTIONS_SERVICE_INJECTION_TOKEN) private readonly planActionsService: PlanActionsService,
        private readonly curriculumPeriodEntityService: CurriculumPeriodEntityService,
    ) {
        this.currentUniversityOrgId = this.universityService.getCurrentUniversityOrgId();
    }

    ngOnInit(): void {
        this.originalValidatablePlan = this.modalValues.validatablePlan;
        this.planManager.setValidatablePlan(_.cloneDeep(this.originalValidatablePlan));
        this.data$ = this.createDataObservable();
        this.createPlanOperationSubjectSubscription();
        this.createPlanUiOperationSubjectSubscription();
        this.createSubmitClickSubscription();
        this.unattachedAttainments = this.rawPlanEditService.getAllUnattachedAttainments(this.getAllAttainments());
        this.isPlanRootModule = _.get(this.modalValues.module, 'id') === this.modalValues.education.id;
        this.planIdInputSubject$.next(this.originalValidatablePlan.plan.id);
        this.dataInput$ = this.planDataService.createInputDataObservableFromPlanIdInput(this.planIdInputSubject$.asObservable());
        this.planStructureData$ = this.planDataService
            .createDataObservable(this.dataInput$)
            .pipe(
                shareReplay({ bufferSize: 1, refCount: true }),
                takeUntilDestroyed(this.destroyRef),
                this.appErrorHandler.defaultErrorHandler(),
            );
    }

    /**
     * Subscribes to planOperationSubject and processes the operations by passing them directly to planManager.
     */
    createPlanOperationSubjectSubscription(): void {
        this.planActionsService.planOperationSubject
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                mergeMap(operation => this.planManager.processPlanOperation(operation)),
            )
            .subscribe();
    }

    createPlanUiOperationSubjectSubscription(): void {
        this.planActionsService.uiOperationSubject
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(operation => this.handleUiOperation(operation)),
            )
            .subscribe();
    }

    handleUiOperation(operation: UiOperation): void {
        switch (operation.uiOperationType) {
            case UiOperationType.OPEN_CUSTOM_STUDY_DRAFT_CREATION_MODAL: {
                this.studentPlanUIActionsImplementationService
                    .openCreateCustomStudyDraftModalHandler(this.modalValues.module.id)
                    .afterClosed()
                    .subscribe(result => {
                        if (result?.operation === 'ADD' && result?.customStudyDraft) {
                            this.planActionsService.addCustomStudyDraft(result.customStudyDraft, this.modalValues.module);
                        }
                    });
                break;
            }
            case UiOperationType.OPEN_COURSE_UNIT_INFO_MODAL: {
                this.studentPlanUIActionsImplementationService
                    .openCourseUnitInfoModalHandler(
                        operation.target,
                        this.modalValues.validatablePlan,
                        this.modalValues.validatablePlanStudyRight,
                    )
                    .afterClosed()
                    .pipe(take(1))
                    .subscribe((stateChange: boolean) => {
                        if (stateChange) {
                            this.dismiss();
                        }
                    });
                break;
            }
            case UiOperationType.OPEN_CUSTOM_ATTAINMENT_INFO_MODAL: {
                this.studentPlanUIActionsImplementationService.openCustomAttainmentInfoModalHandler(operation.target);
                break;
            }
            case UiOperationType.OPEN_MODULE_INFO_MODAL: {
                this.studentPlanUIActionsImplementationService.openModuleInfoModalHandler(
                    operation.target,
                    this.modalValues.validatablePlan,
                    this.modalValues.validatablePlanStudyRight,
                    this.modalValues.planValidationResult,
                );
                break;
            }
            case UiOperationType.OPEN_CUSTOM_STUDY_DRAFT_INFO_MODAL: {
                this.studentPlanUIActionsImplementationService.openCustomStudyDraftInfoModalHandler(
                    operation.target,
                    this.modalValues.validatablePlan.plan.userId,
                    // Don't allow moving to application wizards from edit modal
                    { customAttainmentApplicationsDisabled: true },
                );
                break;
            }
            case UiOperationType.OPEN_ATTAINMENT_DETAILS_MODAL: {
                if (this.modalValues.handleOpenAttainmentDetailsModalUiOperation) {
                    this.modalValues.handleOpenAttainmentDetailsModalUiOperation(operation.target);
                }
                break;
            }
        }
    }

    createDataObservable(): Observable<FreeEditPlanData> {
        // First emitted value will be the initial validatable plan, after that,
        // emit values from planManager.validatablePlanSubject
        const validatablePlan$ = merge(of(this.modalValues.validatablePlan), this.planManager.validatablePlanSubject).pipe(
            shareReplay({ refCount: true, bufferSize: 1 }),
        );

        // Accumulate all course units that have been selected during this edit session
        // Deduplicate by id, with accumulated values taking precedence over new items
        const allCourseUnits$ = validatablePlan$.pipe(
            scan((acc: CourseUnit[], validatablePlan: ValidatablePlan) => {
                const newItems = validatablePlan.getSelectedCourseUnitsUnderModule(this.modalValues.module);
                return _.uniqBy([...acc, ...newItems], 'id');
            }, []),
        );

        // Accumulate all custom course unit attainments that have been selected during this edit session
        // Deduplicate by id, with accumulated values taking precedence over new items
        const allCustomCourseUnitAttainments$ = validatablePlan$.pipe(
            scan((acc: CustomCourseUnitAttainment[], validatablePlan: ValidatablePlan) => {
                const newItems = validatablePlan.getSelectedCustomCourseUnitAttainmentsUnderModule(this.modalValues.module);
                return _.uniqBy([...acc, ...newItems], 'id');
            }, []),
        );

        // Accumulate all modules that have been selected during this edit session
        // Deduplicate by id, with accumulated values taking precedence over new items
        const allModules$ = validatablePlan$.pipe(
            scan((acc: Module[], validatablePlan: ValidatablePlan) => {
                const newItems = validatablePlan.getSelectedModulesUnderModule(this.modalValues.module);
                return _.uniqBy([...acc, ...newItems], 'id');
            }, []),
        );

        // Accumulate all custom module attainments that have been selected during this edit session
        // Deduplicate by id, with accumulated values taking precedence over new items
        const allCustomModuleAttainments$ = validatablePlan$.pipe(
            scan((acc: CustomModuleAttainment[], validatablePlan: ValidatablePlan) => {
                const newItems = validatablePlan.getSelectedCustomModuleAttainmentsUnderModule(this.modalValues.module);
                return _.uniqBy([...acc, ...newItems], 'id');
            }, []),
        );

        // Accumulate all custom study drafts that have been edited during this edit session
        // Deduplicate by id, with accumulated values taking precedence over new items
        const allCustomStudyDrafts$ = validatablePlan$.pipe(
            scan((acc: CustomStudyDraft[], validatablePlan: ValidatablePlan) => {
                const newItems = validatablePlan.getSelectedCustomStudyDraftsByParentModuleId(this.modalValues.module.id);
                return _.uniqBy([...acc, ...newItems], 'id');
            }, []),
        );
        const gradeScalesById$ = validatablePlan$.pipe(switchMap(validatablePlan => this.createGradeScalesByIdObservable(validatablePlan)));

        const searchCurriculumPeriods$ = this.curriculumPeriodEntityService
            .getCurriculumPeriodsForSearch(this.currentUniversityOrgId, this.modalValues.validatablePlan.plan.curriculumPeriodId)
            .pipe(takeUntilDestroyed(this.destroyRef), this.appErrorHandler.defaultErrorHandler());

        return validatablePlan$.pipe(
            combineLatestWith(
                allModules$,
                allCourseUnits$,
                allCustomModuleAttainments$,
                allCustomCourseUnitAttainments$,
                allCustomStudyDrafts$,
                gradeScalesById$,
                searchCurriculumPeriods$,
            ),
            map(
                ([
                    validatablePlan,
                    allModules,
                    allCourseUnits,
                    allCustomModuleAttainments,
                    allCustomCourseUnitAttainments,
                    allCustomStudyDrafts,
                    gradeScalesById,
                    searchCurriculumPeriods,
                ]) => {
                    const planValidationResult = PlanValidationTs.validatePlan(validatablePlan);
                    const educationOptions = PlanStudyRightService.getValidatedEducationOptions(
                        validatablePlan,
                        this.modalValues.education,
                        this.modalValues.validatablePlanStudyRight,
                    );
                    const planStateAndData = this.planStateService.getPlanStateAndData(
                        this.modalValues.education,
                        validatablePlan,
                        planValidationResult,
                        educationOptions,
                        gradeScalesById,
                        this.modalValues.validatablePlanStudyRight,
                    );

                    return {
                        validatablePlan,
                        allModules,
                        allCourseUnits,
                        allCustomModuleAttainments,
                        allCustomCourseUnitAttainments,
                        allCustomStudyDrafts,
                        searchCurriculumPeriods,
                        planData: planStateAndData.planData,
                        planStateObject: planStateAndData.planStateObject,
                    };
                },
            ),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
    }

    /**
     * Gathers all grade scale ids from plan attainments and returns them in an object with the grade scale id as the key.
     *
     * @param validatablePlan Current validatable plan.
     */
    createGradeScalesByIdObservable(validatablePlan: ValidatablePlan): Observable<{ [id: string]: any }> {
        return this.gradeScaleEntityService
            .getByIds(
                sisuChain(_.values(validatablePlan.getAllAttainments())).map('gradeScaleId').concat('sis-0-5').compact().uniq().value(),
            )
            .pipe(map(gradeScales => _.keyBy(gradeScales, 'id')));
    }

    createSubmitClickSubscription() {
        this.submitClick$
            .pipe(
                withLatestFrom(this.data$),
                exhaustMap(([, data]) =>
                    this.updatePlan(data).pipe(
                        take(1),
                        this.appErrorHandler.defaultErrorHandler(),
                        tap(() => this.dialogService.closeAll()),
                    ),
                ),
            )
            .subscribe();
    }

    dismiss() {
        this.dialogService.close();
    }

    selectedCourseUnit(courseUnit: CourseUnitResultItem): void {
        const attainedVersionId = this.rawPlanEditService.findAttainedVersionIdForCourseUnit(
            courseUnit.groupId,
            this.unattachedAttainments,
        );
        const versionIdToBeSelected = attainedVersionId || courseUnit.id;
        this.planActionsService.forceSelectCourseUnitById(versionIdToBeSelected, this.modalValues.module);
    }

    handleCourseUnitToggle(toggleData: { courseUnit: CourseUnit; isInPlan: boolean }): void {
        const { courseUnit, isInPlan } = toggleData;
        if (isInPlan) {
            this.planActionsService.forceRemoveCourseUnit(courseUnit, this.modalValues.module);
        } else {
            this.planActionsService.forceSelectCourseUnitById(courseUnit.id, this.modalValues.module);
        }
    }

    handleCustomCourseUnitAttainmentToggle(toggleData: {
        customCourseUnitAttainment: CustomCourseUnitAttainment;
        isInPlan: boolean;
    }): void {
        const { customCourseUnitAttainment, isInPlan } = toggleData;
        if (isInPlan) {
            this.planActionsService.removeCustomCourseUnitAttainmentById(customCourseUnitAttainment.id, this.modalValues.module);
        } else {
            this.planActionsService.selectCustomCourseUnitAttainment(customCourseUnitAttainment, this.modalValues.module);
        }
    }

    selectedModule(module: GroupingModule | StudyModule | DegreeProgramme): void {
        const attainedVersionId = this.rawPlanEditService.findAttainedVersionIdForModule(module.groupId, this.unattachedAttainments);
        const versionIdToBeSelected = attainedVersionId || module.id;
        this.planActionsService.forceSelectModuleById(versionIdToBeSelected, this.modalValues.module);
    }

    handleModuleToggle(toggleData: { module: GroupingModule | StudyModule | DegreeProgramme; isInPlan: boolean }): void {
        const { module, isInPlan } = toggleData;
        if (isInPlan) {
            this.planActionsService.forceRemoveModule(module, this.modalValues.module);
        } else {
            this.planActionsService.forceSelectModuleById(module.id, this.modalValues.module);
        }
    }

    handleCustomModuleToggle(toggleData: { customModuleAttainment: CustomModuleAttainment; isInPlan: boolean }): void {
        const { customModuleAttainment, isInPlan } = toggleData;
        if (isInPlan) {
            this.planActionsService.removeCustomModuleAttainmentById(customModuleAttainment.id, this.modalValues.module);
        } else {
            this.planActionsService.selectCustomModuleAttainment(customModuleAttainment, this.modalValues.module);
        }
    }

    toggleCustomStudyDraft(toggleData: { customStudyDraft: CustomStudyDraft; isInPlan: boolean }): void {
        const { customStudyDraft, isInPlan } = toggleData;
        if (isInPlan) {
            this.planActionsService.removeCustomStudyDraft(customStudyDraft, this.modalValues.module);
        } else {
            this.planActionsService.addCustomStudyDraft(customStudyDraft, this.modalValues.module);
        }
    }

    getAllAttainments(): Attainment[] {
        return _.values(this.modalValues.validatablePlan.getAllAttainments());
    }

    updatePlan(data: FreeEditPlanData): Observable<Plan> {
        let customStudyDraftConfirmationObservable: Observable<boolean> = of(true);
        const { allCustomStudyDrafts, validatablePlan } = data;
        const selectedCustomStudyDrafts = validatablePlan.getSelectedCustomStudyDraftsByParentModuleId(this.modalValues.module.id);
        if (this.planDiffService.hasUnselectedCustomStudyDrafts(allCustomStudyDrafts, selectedCustomStudyDrafts)) {
            customStudyDraftConfirmationObservable = this.confirmDialogFdOpener({
                title: 'PLAN.CUSTOM_STUDY_DRAFT_DELETE_CONFIRM_MODAL.TITLE',
                descriptions: [
                    'PLAN.CUSTOM_STUDY_DRAFT_DELETE_CONFIRM_MODAL.DESCRIPTION_1',
                    'PLAN.CUSTOM_STUDY_DRAFT_DELETE_CONFIRM_MODAL.DESCRIPTION_2',
                ],
                confirmObservable: of(true),
            } as ConfirmDialogFdValues<boolean>).afterClosed();
        }
        return customStudyDraftConfirmationObservable.pipe(
            switchMap((result: boolean) => (result ? this.planEntityService.updateMyPlan(validatablePlan.plan) : EMPTY)),
        );
    }

    openAddStudyDraftModal() {
        this.planActionsService.openCustomStudyDraftCreationModal(this.modalValues.module);
    }
}
